import React, { useMemo, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { ByteLength_AsString } from '../../util/formatter';
import FormGroup from './FormGroup';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation, Trans } from 'react-i18next';
import icons from '../../constants/icons';

export interface FormFileUploadFile {
  path?: string;
  name?: string;
  size: number;
}

export interface FormFileUploadProps {
  label?: string;
  error?: unknown;
  errorMessage?: string;
  required?: boolean;
  addFiles: (files: File[]) => void;
  removeFile: (file: FormFileUploadFile | File) => void;
  fileExtension?: string;
  hideFiles?: boolean;
  files?: FormFileUploadFile[] | File[];
}

const FormFileUpload = ({
  label,
  error,
  errorMessage,
  required,
  addFiles,
  removeFile,
  fileExtension,
  hideFiles,
  files = [],
}: FormFileUploadProps) => {
  const { t } = useTranslation();
  const onDrop = useCallback(
    (droppedFiles: File[]) => {
      addFiles(droppedFiles);
    },
    [addFiles]
  );

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
  } = useDropzone({ onDrop });

  const filesListed = files.map((f, index) => {
    const fileItem = f as FormFileUploadFile & { name?: string };
    const fileKey = fileItem.path ?? fileItem.name ?? index;
    const fileSize = fileItem.size ?? (f as File).size;
    return (
    <li key={fileKey}>
      <span>
        <FontAwesomeIcon icon={icons.FILE} className="me-3" />
        <b>{fileItem.path ?? fileItem.name}</b>
      </span>
      <div>
        <span>{ByteLength_AsString(fileSize, true, 1, '')}</span>
        <button
          className="ms-3 a"
          title={t('Remove')}
          onClick={() => {
            removeFile(f);
          }}
        >
          <FontAwesomeIcon icon={icons.DELETE_TAG} />
        </button>
      </div>
    </li>
  );
  });

  const classeNames = useMemo(
    () =>
      `dropzone ${isDragActive ? 'active' : ''} ${
        isDragAccept ? 'accept positive-colour1' : ''
      } ${isDragReject ? 'reject' : ''}`,
    [isDragActive, isDragReject, isDragAccept]
  );

  return (
    <FormGroup
      label={label}
      error={Boolean(error)}
      errorMessage={errorMessage}
      required={required}
    >
      <>
        <div {...getRootProps({ className: classeNames })}>
          <input {...getInputProps()} />
          <p>
            <Trans
              i18nKey="dragAndDropInstruction"
              values={{ fileExtension: fileExtension || '.txt' }}
              components={{ bold: <b /> }}
              defaults="Drag & drop {{ fileExtension }} files here, or click to select files"
            />
          </p>
        </div>
        {files.length > 0 && !hideFiles ? (
          <aside className="drop-files">
            <h4>{t('Files')}</h4>
            <ul>{filesListed}</ul>
          </aside>
        ) : null}
      </>
    </FormGroup>
  );
};

export default FormFileUpload;
